import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin } from '../../utils/permissions';
import UserHeadIcon from '../../components/common/UserHeadIcon';
import OnlineUsersLink from './OnlineUsersLink';
import { canViewOnlineUsers } from '../../utils/presence';
import './HomePage.css';

const services = [
  {
    icon: '🖌️',
    title: 'AI 智能体改图',
    url: `${window.location.protocol}//${window.location.host}/image-astro/main`,
    link: `${window.location.protocol}//${window.location.host}/image-astro/main`,
    isInternal: false,
    openInCurrentWindow: true,
    docLink: 'http://172.16.16.16/help/a.html',
    desc: "一键完成各类设计改图与快速迭代"
  },
  {
    icon: '🎬',
    title: 'AI 视频',
    // url: 'https://www.turenimage.com/video-page',
    // link: 'https://www.turenimage.com/video-page',
    url: `${window.location.protocol}//${window.location.host}/ai-media/video/page`,
    link: `${window.location.protocol}//${window.location.host}/ai-media/video/page`,
    desc: "快速生成创意视频内容，拓展多模态表达能力"
  },
  {
    icon: '🔧',
    title: '高清修复',
    url: `${window.location.protocol}//${window.location.host}/ai-media/image-hq/page/`,
    link: `${window.location.protocol}//${window.location.host}/ai-media/image-hq/page/`,
    desc: "智能修复细节，提升图像清晰度"
  },
  {
    icon: '📝',
    title: '提示词反推',
    url: '172.16.16.16:3300',
    link: 'http://172.16.16.16:3300',
    badge: '建设中',
    desc: "帮助快速写出高质量提示词"
  },
  {
    icon: '💬',
    title: 'AI Chat',
    url: 'https://www.turenimage.com/chat-page/',
    link: 'https://www.turenimage.com/chat-page/',
    badge: 'Gemma 模型',
    badgeType: 'highlight',
    desc: "高效生成专业项目与标书文本"
  },
  {
    icon: '🌬️',
    title: '空气检测',
    url: 'https://www.turenimage.com/air-monitor',
    link: 'https://www.turenimage.com/air-monitor',
    desc: "实时监测二氧化碳、甲醛、TVOC、PM2.5 与 AQI"
  },

];

const HomePage = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);

  // ARC-AGI entrance: only visible to authenticated users with ARC_AGI role.
  // NOTE: We check roles_info directly here because getUserRoleCodes() short-circuits
  // for superusers/is_staff and would never see the actual role entries.
  const showArcAgi = isAuthenticated && (
    user?.roles_info?.some((r) => r.code === 'ARC_AGI') ||
    user?.roles?.some((r) => (typeof r === 'string' ? r : r.code) === 'ARC_AGI')
  );

  // Gitea entrance: only visible to authenticated users with third_party_gitea role.
  const showGitea = isAuthenticated && (
    user?.roles_info?.some((r) => (r.code || r.name)?.toLowerCase() === 'third_party_gitea') ||
    user?.roles?.some((r) => (typeof r === 'string' ? r : r.code || r.name)?.toLowerCase() === 'third_party_gitea')
  );
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const toggleDropdown = useCallback(() => {
    setShowDropdown((prev) => !prev);
  }, []);

  const openExternal = useCallback((url) => {
    setShowDropdown(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleServiceClick = useCallback((e, service) => {
    if (!isAuthenticated) {
      e.preventDefault();
      const { redirectToLogin } = require('@myapp/common-web-sdk');
      redirectToLogin();
      return;
    }

    if (!service.isInternal && service.openInCurrentWindow) {
      e.preventDefault();
      window.location.href = service.link;
    }
  }, [isAuthenticated]);

  const handleLogout = useCallback(async () => {
    setShowDropdown(false);
    await logout();
  }, [logout]);

  const dataBoardEnv = process.env.REACT_APP_DATA_BOARD_PAGE;
  const documentCenterUrl = 'https://wiki.turenimage.com';
  let dataBoardUrl = null;
  if (dataBoardEnv) {
    if (dataBoardEnv.startsWith('http://') || dataBoardEnv.startsWith('https://')) {
      try {
        const url = new URL(dataBoardEnv);
        dataBoardUrl = `${window.location.protocol}//${window.location.host}${url.pathname}`;
      } catch (e) {
        dataBoardUrl = dataBoardEnv;
      }
    } else {
      dataBoardUrl = `${window.location.protocol}//${window.location.host}${dataBoardEnv}`;
    }
  }
  const userMsgUrl = process.env.REACT_APP_USER_MSG_PAGE;
  const showUserMsgCenter = false;
  const showDataCenter = isAuthenticated && user && isAdmin(user);

  return (
    <div className="home-page">
      {/* Animated background */}
      <div className="home-bg">
        <div className="home-bg-shape shape-1"></div>
        <div className="home-bg-shape shape-2"></div>
        <div className="home-bg-shape shape-3"></div>
        <div className="home-bg-grid"></div>
      </div>

      {/* Navigation */}
      <nav className="home-nav">
        <div className="home-nav-actions">
          {isAuthenticated ? (
            <>
              <UserHeadIcon user={user} />
              <span className="home-nav-user">欢迎，{user?.user_name || user?.real_name || user?.username}</span>
              <div className="home-nav-dropdown-wrapper" ref={dropdownRef}>
                <button
                  className="home-nav-menu-btn"
                  onClick={toggleDropdown}
                  aria-label="应用菜单"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="home-nav-apps-icon">
                    <circle cx="5" cy="5" r="2" />
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="19" cy="5" r="2" />
                    <circle cx="5" cy="12" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="19" cy="12" r="2" />
                    <circle cx="5" cy="19" r="2" />
                    <circle cx="12" cy="19" r="2" />
                    <circle cx="19" cy="19" r="2" />
                  </svg>
                </button>
                <div className={`home-nav-dropdown ${showDropdown ? 'show' : ''}`}>
                  <div className="home-nav-dropdown-content">
                    <button type="button" className="home-nav-dropdown-item" onClick={() => {
                      setShowDropdown(false);
                      window.location.href = `${window.location.protocol}//${window.location.host}/account-center`;
                    }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7" rx="1" />
                        <rect x="14" y="3" width="7" height="7" rx="1" />
                        <rect x="14" y="14" width="7" height="7" rx="1" />
                        <rect x="3" y="14" width="7" height="7" rx="1" />
                      </svg>
                      <span>账号中心</span>
                    </button>
                    <button type="button" className="home-nav-dropdown-item" onClick={() => {
                      setShowDropdown(false);
                      window.location.href = `${window.location.protocol}//${window.location.host}/user-opinion`;
                    }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <span>我有话说</span>
                    </button>
                    <button type="button" className="home-nav-dropdown-item" onClick={() => {
                      setShowDropdown(false);
                      window.location.href = `${window.location.protocol}//${window.location.host}/project-page`;
                    }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                      <span>项目管理</span>
                    </button>
                    <button type="button" className="home-nav-dropdown-item" onClick={() => openExternal(documentCenterUrl)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                        <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" />
                        <path d="M8 7h8" />
                        <path d="M8 11h6" />
                      </svg>
                      <span>文档中心</span>
                    </button>
                    {showDataCenter && dataBoardUrl && (
                      <button type="button" className="home-nav-dropdown-item" onClick={() => openExternal(dataBoardUrl)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 3v18h18" />
                          <path d="M18 17V9" />
                          <path d="M13 17V5" />
                          <path d="M8 17v-3" />
                        </svg>
                        <span>数据看版</span>
                      </button>
                    )}
                    {showUserMsgCenter && userMsgUrl && (
                      <button type="button" className="home-nav-dropdown-item" onClick={() => openExternal(userMsgUrl)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        <span>消息中心</span>
                      </button>
                    )}
                    <button onClick={handleLogout} className="home-nav-dropdown-item home-nav-logout">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      <span>退出登录</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <a
              href={`${window.location.protocol}//${window.location.host}/unify-login/login?next=${encodeURIComponent(process.env.PUBLIC_URL)}`}
              className="home-nav-btn primary"
            >
              登录
            </a>
          )}
        </div>
      </nav>

      {/* Hero */}
      <header className="home-hero">
        <div className="home-hero-content">
          <span className="home-kicker">AI DESIGN STUDIO</span>
          <h1 className="home-title">
            AI 设计工作台
          </h1>
          <p className="home-subtitle">智能创意引擎，赋能高效设计工作流。</p>
        </div>
      </header>

      {/* Services Grid */}
      <main className="home-main">

        <div className="services-grid">
          {showArcAgi && (
            <article
              className="service-card"
              style={{ animationDelay: `${services.length * 0.1}s` }}
            >
              <a
                href={`${window.location.protocol}//${window.location.host}/arc-agi/page/`}
                className="service-link"
              >
                <div className="service-icon-wrapper">
                  <span className="service-icon">🧩</span>
                  <div className="service-icon-glow"></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">ARC-AGI</h3>
                  <p className="service-desc">ARC-AGI 智能评测平台</p>
                </div>
                <div className="service-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              </a>
            </article>
          )}
          {showGitea && (
            <article
              className="service-card"
              style={{ animationDelay: `${(services.length + 1) * 0.1}s` }}
            >
              <a
                href={`${window.location.protocol}//${window.location.host}/gitea`}
                className="service-link"
              >
                <div className="service-icon-wrapper">
                  <span className="service-icon">🍵</span>
                  <div className="service-icon-glow"></div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">Gitea</h3>
                  <p className="service-desc">Gitea 代码托管平台</p>
                </div>
                <div className="service-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              </a>
            </article>
          )}
          {services.map((service, index) => (
            <article
              key={service.title}
              className="service-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {service.isInternal ? (
                <Link
                  to={service.link}
                  className="service-link"
                  onClick={(e) => handleServiceClick(e, service)}
                >
                  <div className="service-icon-wrapper">
                    <span className="service-icon">{service.icon}</span>
                    <div className="service-icon-glow"></div>
                  </div>
                  <div className="service-content">
                    <h3 className="service-title">{service.title}</h3>
                    {service.desc && (
                      <p className="service-desc">{service.desc}</p>
                    )}
                    {service.badge && (
                      <span className={`service-badge ${service.badgeType || ''}`}>
                        {service.badge}
                      </span>
                    )}
                  </div>
                  <div className="service-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ) : (
                <a
                  href={service.link}
                  {...(service.openInCurrentWindow ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                  className="service-link"
                  onClick={(e) => handleServiceClick(e, service)}
                >
                  <div className="service-icon-wrapper">
                    <span className="service-icon">{service.icon}</span>
                    <div className="service-icon-glow"></div>
                  </div>
                  <div className="service-content">
                    <h3 className="service-title">{service.title}</h3>
                    {service.desc && (
                      <p className="service-desc">{service.desc}</p>
                    )}
                    {service.badge && (
                      <span className={`service-badge ${service.badgeType || ''}`}>
                        {service.badge}
                      </span>
                    )}
                  </div>
                  <div className="service-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                </a>
              )}
              {service.docLink && (
                <a href={service.docLink} target="_blank" rel="noopener noreferrer" className="service-doc">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  使用手册
                </a>
              )}
            </article>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="home-footer">
        <p className="footer-tagline">AI 创意工具 · 设计协作 · 效率提升</p>
        <p className="footer-credits">
          Crafted with <span className="footer-heart">♥</span> by{' '}
          <a href="http://www.turen.design" target="_blank" rel="noopener noreferrer">
            Turen Design
          </a>
        </p>
        {isAuthenticated && canViewOnlineUsers(user) ? (
          <p className="footer-credits footer-online-line">
            <OnlineUsersLink key={user?.id || user?.user_id} />
          </p>
        ) : (
          <p className="footer-credits" style={{ marginTop: '0.5rem', opacity: 0.8 }}>
            为日常设计工作提供稳定、直接的创作支持。
          </p>
        )}
      </footer>
    </div>
  );
};

export default HomePage;
